/* eslint-disable camelcase */
import { forwardRef, useEffect, useState } from 'react'
import { setPlugins, getEditorDefaults, PinturaDefaultImageWriterResult } from '@pqina/pintura'
import {
  plugin_trim,
  plugin_trim_locale_en_gb,
  createDefaultVideoWriter,
  createMediaStreamEncoder
} from '@pqina/pintura-video'
import '@pqina/pintura-video/pinturavideo.css'
import { imageStateToCanvas, createDefaultImageWriter, createDefaultMediaWriter } from '@pqina/pintura'
import { PinturaEditor } from '@pqina/react-pintura'
import styles from './VideoEditor.pcss'
import { 
  defaultVisibleStickers,
  configureStickerWillRenderShapePresetToolbar,
  markupEditorToolbarDefault, 
  markupEditorToolStylesDefault,
  defaultTargetSize
} from 'components/ImageEditor/pintura.config'

interface VideoEditorProps {
  videoUrl?: string
  hideSaveButton?: boolean
  onExport?: (file: File) => void
}

export const VideoEditor = forwardRef<PinturaEditor, VideoEditorProps>((
  { videoUrl, onExport, hideSaveButton }: VideoEditorProps, 
  ref: React.ForwardedRef<PinturaEditor>
) => {
  const [videoPluginLoaded, setVideoPluginLoaded] = useState(false)
  const defaultConfig = getEditorDefaults()

  useEffect(() => {
    if (!videoPluginLoaded) {
      setPlugins(plugin_trim)
      setVideoPluginLoaded(true)
    }
  }, [videoPluginLoaded])

  const containerSelector = '[data-testid="video-editor"]'

  const onProcess = (res: PinturaDefaultImageWriterResult) => {
    if (onExport) {
      onExport(res.dest)
    }
  }

  if (!videoPluginLoaded) {
    return null
  }

  return (
    <div data-testid="video-editor" className={styles.container}>
      <PinturaEditor
        {...defaultConfig}
        ref={ref}
        src={videoUrl}
        util="trim"
        annotateActiveTool="text"
        utils={(defaultConfig.utils || []).filter(u => u !== 'decorate')}
        locale={{ ...defaultConfig.locale, ...plugin_trim_locale_en_gb }}
        willRenderToolbar={(toolbar, _env, _redraw) => hideSaveButton ? toolbar.slice(0, 2) : toolbar}
        stickerWillRenderShapePresetToolbar={configureStickerWillRenderShapePresetToolbar(containerSelector)}
        stickers={defaultVisibleStickers}
        markupEditorToolbar={markupEditorToolbarDefault}
        markupEditorToolStyles={markupEditorToolStylesDefault}
        imageWriter={createDefaultMediaWriter(
          { targetSize: defaultTargetSize },
          [
            createDefaultImageWriter(),
            createDefaultVideoWriter({
              encoder: createMediaStreamEncoder({
                imageStateToCanvas,
                videoBitrate: 8_000_000, // 8 Mbps
                audioBitrate: 320_000, // 320 Kbps
                framesPerSecond: 30 // 30fps for smoother motion
              }),
              targetSize: defaultTargetSize
            })
          ]
        )}
        onProcess={onProcess}
      />
    </div>
  )
})
